import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Text } from '../Text/Text';
import type { IMoreActionsProps, IMoreActionItem } from './MoreActions.types';

/**
 * MoreActions - A flexible dropdown menu component
 * 
 * @description
 * A reusable dropdown menu component that can be positioned relative to a trigger element.
 * Supports multiple positions, icons, keyboard shortcuts, separators, and different variants.
 * 
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * const triggerRef = useRef<HTMLButtonElement>(null);
 * 
 * const items = [
 *   { id: 'open', label: 'Open in new window', onClick: () => {} },
 *   { id: 'download', label: 'Download', onClick: () => {} },
 *   { type: 'separator', label: '' },
 *   { id: 'save', label: 'Save', variant: 'primary', onClick: () => {} },
 * ];
 * 
 * <button ref={triggerRef} onClick={() => setIsOpen(!isOpen)}>
 *   Open Menu
 * </button>
 * <MoreActions
 *   items={items}
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   position="bottom-right"
 *   triggerRef={triggerRef}
 * />
 * ```
 * 
 * @features
 * - 6 positioning options: top-left, top-right, bottom-left, bottom-right, left, right
 * - Click outside to close
 * - Escape key to close
 * - Support for icons, shortcuts, and separators
 * - Three variants: default, primary, destructive
 * - Disabled state support
 * - Customizable width
 * - Uses design tokens from tailwind.config.js
 */
export const MoreActions: React.FC<IMoreActionsProps> = ({
  items,
  isOpen,
  onClose,
  position = 'bottom-right',
  triggerRef,
  className = '',
  width = 170,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Calculate menu position based on trigger element
  useEffect(() => {
    if (!isOpen || !triggerRef?.current) return;

    const updatePosition = () => {
      const triggerRect = triggerRef.current!.getBoundingClientRect();
      const menuHeight = menuRef.current?.offsetHeight || 200;
      const menuWidth = width;
      
      let top = 0;
      let left = 0;

      switch (position) {
        case 'top-left':
          top = triggerRect.top - menuHeight - 4;
          left = triggerRect.left;
          break;
        case 'top-right':
          top = triggerRect.top - menuHeight - 4;
          left = triggerRect.right - menuWidth;
          break;
        case 'bottom-left':
          top = triggerRect.bottom + 4;
          left = triggerRect.left;
          break;
        case 'bottom-right':
          top = triggerRect.bottom + 4;
          left = triggerRect.right - menuWidth;
          break;
        case 'left':
          top = triggerRect.top;
          left = triggerRect.left - menuWidth - 4;
          break;
        case 'right':
          top = triggerRect.top;
          left = triggerRect.right + 4;
          break;
      }

      // Ensure menu stays within viewport
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      if (left + menuWidth > viewportWidth) {
        left = viewportWidth - menuWidth - 8;
      }
      if (left < 8) {
        left = 8;
      }
      if (top + menuHeight > viewportHeight) {
        top = viewportHeight - menuHeight - 8;
      }
      if (top < 8) {
        top = 8;
      }

      setMenuPosition({ top, left });
    };

    updatePosition();

    // Close on any scroll (prevents menu from floating away from its trigger)
    const handleScroll = () => onClose();
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, position, triggerRef, width, onClose]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef?.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleItemClick = (item: IMoreActionItem, event: React.MouseEvent) => {
    event.stopPropagation();

    if (item.disabled) return;

    if (item.onClick) {
      item.onClick();
    }

    onClose();
  };

  const menuContent = (
    <div
      ref={menuRef}
      className={`
        fixed
        bg-bg-primary 
        border 
        border-border-secondary 
        rounded-bg-v2-sm 
        shadow-bg-v2-lg 
        py-bg-v2-xs
        px-bg-v2-xxs
        flex 
        flex-col 
        gap-0
        z-[9999]
        ${className}
      `}
      style={{ 
        width: `${width}px`, 
        minWidth: '170px',
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
      }}
      role="menu"
      aria-orientation="vertical"
    >
      {items.map((item, index) => (
        <React.Fragment key={item.id || index}>
          {item.type === 'separator' ? (
            <div 
              className="h-px bg-border-secondary w-full my-bg-v2-xxs" 
              role="separator"
            />
          ) : (
            <button
              onClick={(e) => handleItemClick(item, e)}
              disabled={item.disabled}
              className={`
                w-full
                text-left
                px-bg-v2-md
                py-[6px]
                rounded-bg-v2-xs
                transition-colors
                font-bg-v2-body
                ${
                  item.disabled
                    ? 'cursor-not-allowed opacity-40'
                    : item.variant === 'destructive'
                    ? 'hover:bg-bg-error-primary text-text-error-primary'
                    : item.variant === 'primary'
                    ? 'hover:bg-bg-brand-section text-text-brand-tertiary-alt'
                    : 'hover:bg-bg-secondary text-text-primary'
                }
              `}
              role="menuitem"
              aria-disabled={item.disabled}
            >
              <div className="flex items-center gap-bg-v2-md">
                {item.icon && (
                  <span className="flex items-center justify-center flex-shrink-0 w-4 h-4">
                    {item.icon}
                  </span>
                )}
                <Text
                  variant="text-sm"
                  weight="regular"
                  color={
                    item.disabled
                      ? 'disabled'
                      : item.variant === 'destructive'
                      ? 'error'
                      : item.variant === 'primary'
                      ? 'brand-tertiary'
                      : 'primary'
                  }
                  className="flex-1"
                >
                  {item.label}
                </Text>
                {item.shortcut && (
                  <Text
                    variant="text-xs"
                    weight="regular"
                    color="tertiary"
                    className="flex-shrink-0"
                  >
                    {item.shortcut}
                  </Text>
                )}
              </div>
            </button>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return createPortal(menuContent, document.body);
};

